import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { useQuery } from "react-query";
import { AxiosResponse } from "axios";
import MapComponent from "src/components/common/KML";
import ViewImages from "src/components/common/ViewImage";
import {
  approveNewKml,
  getNewKmlThread,
  NewKmlThread,
  raiseNewKmlQuery,
} from "src/utils/api/newKml";
import { dateAndTimeStamp, downloadS3File } from "src/utils/helper";

const statusLabel: Record<string, string> = {
  PENDING_REVIEW: "Uploaded",
  UNDER_QUERY: "Under Query",
  APPROVED: "Approved",
};

type DialogMode = "approve" | "query" | null;

const NewKmlSection = ({ epid }: { epid: string }) => {
  const [dialogMode, setDialogMode] = useState<DialogMode>(null);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { data, isLoading, refetch } = useQuery(
    ["newKmlThread", epid],
    () => getNewKmlThread(epid),
    { enabled: !!epid, refetchOnMount: true }
  );

  const thread = (data as AxiosResponse)?.data as NewKmlThread | undefined;

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  const isLocked = thread?.isLocked ?? false;

  const closeDialog = () => {
    if (submitting) return;
    setDialogMode(null);
    setComment("");
    setError(null);
  };

  const handleConfirm = async () => {
    if (dialogMode === "query" && !comment.trim()) {
      setError("Please enter a comment for the query.");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      if (dialogMode === "approve") {
        await approveNewKml(epid, comment.trim() || undefined);
      } else if (dialogMode === "query") {
        await raiseNewKmlQuery(epid, comment.trim());
      }
      setSubmitting(false);
      setDialogMode(null);
      setComment("");
      refetch();
    } catch (err: any) {
      setSubmitting(false);
      setError(
        err?.response?.data?.message ?? "Action failed. Please try again."
      );
    }
  };

  return (
    <Box sx={{ mt: 3 }}>
      {thread?.newKml?.url ? (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 3 }}>
          {/* Approved KML — left */}
          <Box sx={{ flex: "1 1 320px", minWidth: 320 }}>
            <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
              Approved KML
            </Typography>
            {thread.originalKml?.url ? (
              <>
                <ViewImages
                  fileUrl={thread.originalKml.url}
                  label={""}
                  onDownload={() =>
                    downloadS3File(thread.originalKml.url!, `kml_original_${epid}.kml`)
                  }
                />
                <MapComponent kmlUrl={thread.originalKml.url} />
              </>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Not available
              </Typography>
            )}
          </Box>

          {/* Uploaded KML — right */}
          <Box sx={{ flex: "1 1 320px", minWidth: 320 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                Uploaded KML
              </Typography>
              {thread.newKmlStatus && (
                <Chip
                  size="small"
                  color={isLocked ? "success" : "warning"}
                  label={statusLabel[thread.newKmlStatus] ?? thread.newKmlStatus}
                />
              )}
            </Box>
            <ViewImages
              fileUrl={thread.newKml.url}
              label={""}
              onDownload={() =>
                downloadS3File(thread.newKml!.url!, `kml_new_${epid}.kml`)
              }
            />
            <MapComponent kmlUrl={thread.newKml.url} />
          </Box>
        </Box>
      ) : thread?.originalKml?.url ? (
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
            Approved KML
          </Typography>
          <ViewImages
            fileUrl={thread.originalKml.url}
            label={""}
            onDownload={() =>
              downloadS3File(thread.originalKml.url!, `kml_original_${epid}.kml`)
            }
          />
          <MapComponent kmlUrl={thread.originalKml.url} />
        </Box>
      ) : null}

      {!!thread?.thread?.length && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
            Comments
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            {thread?.thread.map((c) => (
              <Box
                key={c.id}
                sx={{
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 1,
                  p: 1.2,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    gap: 2,
                  }}
                >
                  <Typography variant="caption" sx={{ fontWeight: 600 }}>
                    {c.authorUserName || c.authorSubType}{" "}
                    <Typography
                      component="span"
                      variant="caption"
                      color="text.secondary"
                    >
                      ({c.authorSubType})
                    </Typography>
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {c.createdAt ? dateAndTimeStamp(c.createdAt, "IST") : ""}
                  </Typography>
                </Box>
                <Typography variant="body2">{c.comment}</Typography>
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {!isLocked && !!thread?.newKml?.url && (
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mt: 2 }}>
          <Button
            variant="contained"
            color="primary"
            size="small"
            onClick={() => setDialogMode("approve")}
          >
            Approve
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={() => setDialogMode("query")}
          >
            Raise Query
          </Button>
        </Box>
      )}

      {/* Approve confirmation dialog */}
      <Dialog open={dialogMode === "approve"} onClose={closeDialog} maxWidth="xs" fullWidth>
        <DialogTitle>Confirm Approval</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            Are you sure you want to approve this KML? This action will lock
            the file — the Implementing Officer will no longer be able to
            replace it.
          </Typography>
          {error && (
            <Typography variant="caption" color="error" sx={{ mt: 1, display: "block" }}>
              {error}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} disabled={submitting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleConfirm}
            disabled={submitting}
          >
            {submitting ? "Approving…" : "Yes, Approve"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Raise query dialog */}
      <Dialog open={dialogMode === "query"} onClose={closeDialog} fullWidth maxWidth="sm">
        <DialogTitle>Raise Query</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            Add a comment explaining what needs to change. The Implementing
            Officer can then replace the KML.
          </Typography>
          <Box sx={{ mb: 0.5 }}>
            <Typography variant="caption" sx={{ fontWeight: 500, color: "text.secondary" }}>
              Comment *
            </Typography>
          </Box>
          <Box
            component="textarea"
            rows={3}
            value={comment}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setComment(e.target.value)}
            placeholder="Describe what needs to change…"
            sx={{
              width: "100%",
              boxSizing: "border-box",
              padding: "10px 12px",
              fontSize: "14px",
              fontFamily: "inherit",
              lineHeight: 1.5,
              border: "1px solid",
              borderColor: "divider",
              borderRadius: "4px",
              resize: "vertical",
              outline: "none",
              "&:focus": { borderColor: "primary.main" },
              "&::placeholder": { color: "#aaa", opacity: 1 },
            }}
          />
          {error && (
            <Typography variant="caption" color="error" sx={{ mt: 1, display: "block" }}>
              {error}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} disabled={submitting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirm}
            disabled={submitting}
          >
            {submitting ? "Submitting…" : "Submit Query"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default NewKmlSection;
